import { createNavigation } from "next-intl/navigation";
import { defaultLocale } from "./config";
import { splitLocalePathname } from "./path";
import { routing } from "./routing";

type Navigation = ReturnType<
	typeof createNavigation<(typeof routing)["locales"], "never", never, never>
>;

const navigation: Navigation = createNavigation<
	(typeof routing)["locales"],
	"never",
	never,
	never
>(routing);

export const Link: Navigation["Link"] = navigation.Link;
/**
 * Plain next/link, re-exported here so callers that need a raw URL escape hatch
 * don't reach for `next/link` directly and trip the noRestrictedImports rule.
 * Most app links should use the next-intl `Link` above; public URLs still stay
 * unprefixed through `localePrefix: "never"`.
 */
export { default as UnlocalizedLink } from "next/link";
/**
 * next-intl's locale-context hook, re-exported so components read the route
 * locale through the facade instead of importing next-intl directly.
 */
export { useLocale } from "next-intl";
export const getPathname: Navigation["getPathname"] = navigation.getPathname;
export const usePathname: Navigation["usePathname"] = navigation.usePathname;
export const useRouter: Navigation["useRouter"] = navigation.useRouter;
export const localizedRedirect: Navigation["redirect"] = navigation.redirect;
export const localizedPermanentRedirect: Navigation["permanentRedirect"] =
	navigation.permanentRedirect;

export type RedirectArgs = Parameters<Navigation["redirect"]>[0];
export type RedirectType = Parameters<Navigation["redirect"]>[1];
type RedirectHref = RedirectArgs["href"];
export type RedirectTarget = RedirectArgs | RedirectHref | URL;

export type RedirectResolution =
	| {
			kind: "native";
			href: string;
	  }
	| {
			kind: "localized";
			args: RedirectArgs;
	  };

export function resolveRedirectTarget(
	target: RedirectTarget,
): RedirectResolution {
	if (target instanceof URL) {
		return {
			kind: "native",
			href: target.toString(),
		};
	}
	if (isExternalHref(target)) {
		return {
			kind: "native",
			href: target,
		};
	}
	return {
		kind: "localized",
		args: toRedirectArgs(target),
	};
}

function toRedirectArgs(target: RedirectArgs | RedirectHref): RedirectArgs {
	if (typeof target === "object" && "href" in target) {
		return target;
	}

	if (typeof target === "string") {
		const { locale, pathname } = splitLocalePathname(target);
		return {
			href: pathname,
			locale: locale ?? defaultLocale,
		};
	}

	return {
		href: target,
		locale: defaultLocale,
	};
}

function isExternalHref(target: RedirectTarget): target is string {
	return typeof target === "string" && /^[a-z][a-z\d+.-]*:/i.test(target);
}
